/**
 * useProfile Hook - Thin wrapper for App.js integration
 * 
 * This hook provides the same interface that App.js currently expects,
 * but internally uses the Zustand profileStore. This enables:
 * 
 * 1. Zero-breaking-change migration path
 * 2. Same API for consuming components
 * 3. Easy to remove once App.js is fully migrated
 * 
 * Usage in App.js:
 *   const profile = useProfile(currentUser, triggerToast);
 *   // profile.varietyMode, profile.handleVarietyModeChange, etc.
 */

import { useEffect, useCallback } from 'react';
import { useAppContextStore } from '../stores/appContextStore';
import { useProfileStore } from '../stores/profileStore';
import { fetchUserProfile, updateToggles } from '../services/api';
import type { VarietyMode, MealSettings, WineSettings, Toggles, DietPhilosophy, MacroProfile } from '../types';

interface CurrentUser {
    getIdToken: () => Promise<string>;
}

export function useProfile(currentUser: CurrentUser | null, triggerToast?: (msg: string) => void) {
    // Actions are stable in Zustand if retrieved via granular selectors
    const storeLoadProfile = useProfileStore(state => state.loadProfile);
    const storeSetVarietyMode = useProfileStore(state => state.setVarietyMode);
    const storeSetToggles = useProfileStore(state => state.setToggles);
    const storeSetMealSettings = useProfileStore(state => state.setMealSettings);
    const storeSetWineSettings = useProfileStore(state => state.setWineSettings);
    const storeSetDietPhilosophy = useProfileStore(state => state.setDietPhilosophy);
    const storeSetMacroProfile = useProfileStore(state => state.setMacroProfile);
    const storeSetHouseholdSize = useProfileStore(state => state.setHouseholdSize);

    // Subscription State
    const subscriptionStatus = useProfileStore(state => state.subscriptionStatus);
    const trialEndsAt = useProfileStore(state => state.trialEndsAt);

    // State subscription
    const userTier = useProfileStore(state => state.userTier);
    const userName = useProfileStore(state => state.userName);
    const toggles = useProfileStore(state => state.toggles);
    const varietyMode = useProfileStore(state => state.varietyMode);
    const varietyModeSaving = useProfileStore(state => state.varietyModeSaving);
    const wineSettings = useProfileStore(state => state.wineSettings);
    const mealSettings = useProfileStore(state => state.mealSettings);
    const dietPhilosophy = useProfileStore(state => state.dietPhilosophy);
    const macroProfile = useProfileStore(state => state.macroProfile);
    const householdSize = useProfileStore(state => state.householdSize);
    const profileLoading = useProfileStore(state => state.loading);
    const isContextLoading = useAppContextStore(state => state.isLoading);
    const consolidatedLoading = profileLoading || isContextLoading;
    const profileError = useProfileStore(state => state.error);
    const isTrialActive = trialEndsAt ? new Date(trialEndsAt) > new Date() : false;
    const isSommelierEntitled = userTier === 'premium' || userTier === 'unlimited' || isTrialActive;

    // Load profile on mount when currentUser is available
    useEffect(() => {
        if (!currentUser) return;

        const getToken = () => currentUser.getIdToken();
        storeLoadProfile(getToken, fetchUserProfile);
    }, [currentUser, storeLoadProfile]);

    // Wrap handlers to inject dependencies
    const handleVarietyModeChange = useCallback(
        async (newMode: VarietyMode) => {
            if (!currentUser) return;
            const getToken = () => currentUser.getIdToken();
            const response = await storeSetVarietyMode(newMode, getToken, updateToggles, triggerToast);
            // Surgical hydration instead of prefetch
            if (response) useAppContextStore.getState().hydrateStores({ profile: response });
        },
        [currentUser, triggerToast, storeSetVarietyMode]
    );

    const handleTogglePreference = useCallback(
        async (id: keyof Toggles) => {
            if (!currentUser) return;

            // Premium check for wine (Trial aware)
            if (id === 'wine' && !isSommelierEntitled) {
                triggerToast?.('Wine Sommelier is a Premium feature!');
                return;
            }

            const newToggles = { ...toggles, [id]: !toggles[id] };
            storeSetToggles(newToggles);

            try {
                const token = await currentUser.getIdToken();
                const response = await updateToggles(token, {
                    ...newToggles,
                    wine_pairing_settings: wineSettings,
                    meal_structure_settings: mealSettings,
                });
                // Surgical hydration
                useAppContextStore.getState().hydrateStores({ profile: response });
                const featureNames: Record<string, string> = {
                    wine: 'Wine Sommelier',
                    macro: 'Macro Enforcement',
                    pantry: 'Pantry Intelligence',
                };
                triggerToast?.(`${featureNames[id] || id} updated!`);
            } catch (err) {
                // Revert on failure
                storeSetToggles(toggles);
                console.error('Failed to update toggles:', err);
            }
        },
        [currentUser, toggles, wineSettings, mealSettings, storeSetToggles, triggerToast, isSommelierEntitled]
    );

    const handleMealSettingToggle = useCallback(
        async (mealType: keyof MealSettings) => {
            if (!currentUser) return;

            const newSettings = { ...mealSettings, [mealType]: !mealSettings[mealType] };
            storeSetMealSettings(newSettings);

            try {
                const token = await currentUser.getIdToken();
                const response = await updateToggles(token, {
                    ...toggles,
                    wine_pairing_settings: wineSettings,
                    meal_structure_settings: newSettings,
                });
                // Surgical hydration
                useAppContextStore.getState().hydrateStores({ profile: response });
                triggerToast?.(`${mealType} ${newSettings[mealType] ? 'added to' : 'removed from'} structure`);
            } catch (err) {
                storeSetMealSettings(mealSettings);
                console.error('Failed to update meal structure:', err);
            }
        },
        [currentUser, mealSettings, toggles, wineSettings, storeSetMealSettings, triggerToast]
    );

    const handleWineSettingToggle = useCallback(
        async (mealType: keyof WineSettings) => {
            if (!currentUser) return;

            const newSettings = { ...wineSettings, [mealType]: !wineSettings[mealType] };
            storeSetWineSettings(newSettings);

            try {
                const token = await currentUser.getIdToken();
                const response = await updateToggles(token, {
                    ...toggles,
                    wine_pairing_settings: newSettings,
                    meal_structure_settings: mealSettings,
                });
                // Surgical hydration
                useAppContextStore.getState().hydrateStores({ profile: response });
                triggerToast?.(`${mealType} wine pairing ${newSettings[mealType] ? 'enabled' : 'disabled'}`);
            } catch (err) {
                storeSetWineSettings(wineSettings);
                console.error('Failed to update wine settings:', err);
            }
        },
        [currentUser, wineSettings, toggles, mealSettings, storeSetWineSettings, triggerToast]
    );

    const handleDietPhilosophyChange = useCallback(
        async (phi: DietPhilosophy | null) => {
            if (!currentUser) return;
            const getToken = () => currentUser.getIdToken();
            const response = await storeSetDietPhilosophy(phi, getToken, updateToggles, triggerToast);
            // Surgical hydration
            if (response) useAppContextStore.getState().hydrateStores({ profile: response });
        },
        [currentUser, triggerToast, storeSetDietPhilosophy]
    );

    const handleMacroProfileChange = useCallback(
        async (prof: MacroProfile | null) => {
            if (!currentUser) return;
            const getToken = () => currentUser.getIdToken();
            const response = await storeSetMacroProfile(prof, getToken, updateToggles, triggerToast);
            // Surgical hydration
            if (response) useAppContextStore.getState().hydrateStores({ profile: response });
        },
        [currentUser, triggerToast, storeSetMacroProfile]
    );

    const handleHouseholdSizeChange = useCallback(
        async (size: number) => {
            if (!currentUser) return;
            const getToken = () => currentUser.getIdToken();
            const response = await storeSetHouseholdSize(size, getToken, updateToggles, triggerToast);
            // Surgical hydration
            if (response) useAppContextStore.getState().hydrateStores({ profile: response });
        },
        [currentUser, triggerToast, storeSetHouseholdSize]
    );


    return {
        // State
        userTier,
        userName,
        toggles,
        varietyMode,
        varietyModeSaving,
        wineSettings,
        mealSettings,
        profileLoading: consolidatedLoading,
        profileError,
        subscriptionStatus,
        trialEndsAt,

        // Handlers
        handleVarietyModeChange,
        handleTogglePreference,
        handleMealSettingToggle,
        handleWineSettingToggle,
        handleDietPhilosophyChange,
        handleMacroProfileChange,
        handleHouseholdSizeChange,
        dietPhilosophy,
        macroProfile,
        householdSize,
    };
}
